import axios from 'axios';
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef, } from 'react';
function authReducer(state, action) {
    switch (action.type) {
        case 'SIGN_IN':
            return { ...state, token: action.payload };
        case 'SIGN_OUT':
            return {
                ...state,
                token: null,
            };
        default:
            return state;
    }
}
const AuthContext = createContext({});
const ACCESS_TOKEN_KEY = 'access_token';
const initialState = {
    token: localStorage.getItem(ACCESS_TOKEN_KEY) ?? null,
};
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const { token } = state;
    const authRequestRef = useRef(null);
    const authResponseRef = useRef(null);
    const initializeInterceptors = useCallback(() => {
        if (!token) {
            throw new Error('Missing auth token');
        }
        // do not reinitialize
        if (authRequestRef.current !== null || authResponseRef.current !== null) {
            return;
        }
        // Add Bearer auth token to all requests
        authRequestRef.current = axios.interceptors.request.use(function (config) {
            if (config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
        // Log out user on failed auth request
        authResponseRef.current = axios.interceptors.response.use((response) => response, (err) => {
            if (err.response?.status === 401) {
                // if you ever get an unauthorized response, logout the user
                dispatch({ type: 'SIGN_OUT' });
            }
            throw err;
        });
    }, [token]);
    const ejectInterceptors = useCallback(() => {
        if (authRequestRef.current !== null) {
            axios.interceptors.request.eject(authRequestRef.current);
            authRequestRef.current = null;
        }
        if (authResponseRef.current !== null) {
            axios.interceptors.response.eject(authResponseRef.current);
            authResponseRef.current = null;
        }
    }, []);
    useEffect(() => {
        if (token) {
            localStorage.setItem(ACCESS_TOKEN_KEY, token);
            initializeInterceptors();
        }
        else {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            ejectInterceptors();
        }
    }, [token, initializeInterceptors, ejectInterceptors]);
    const handlers = useMemo(() => ({
        signIn: (value) => dispatch({ type: 'SIGN_IN', payload: value }),
        signOut: () => dispatch({ type: 'SIGN_OUT' }),
    }), []);
    const interceptors = useMemo(() => ({ initializeInterceptors, ejectInterceptors }), [initializeInterceptors, ejectInterceptors]);
    return (React.createElement(AuthContext.Provider, { value: [state, handlers, interceptors] }, children));
};
export const useAuthContext = () => React.useContext(AuthContext);
